import { CHANNELS } from "../shared/channels";
import { TrackerSong, cleanSong } from "../shared/tracker";
import { call } from "./bridge";
import { flashToast } from "./dom";
import { t } from "./i18n";

// the song being edited, its undo history and its saving. every tracker panel reads `state` and changes the song only
// through edit(), so undo, redo, the limits bar and the autosave always see the same thing

export type Scope = "notes" | "song" | "sounds" | "all" | "quiet";

export const state = {
  song: null as TrackerSong | null,
  // the pattern being edited
  pat: 0,
  // the selected square
  sel: null as { r: number; c: number } | null,
  octave: 4,
  // the sound new notes get
  pen: 0
};

const HISTORY = 60;
let past: string[] = [];
let future: string[] = [];
let lastEdit = { key: "", at: 0 };
let saveTimer = 0;
let saving: Promise<void> = Promise.resolve();
const listeners = new Set<(scope: Scope) => void>();

export function onSong(listener: (scope: Scope) => void): void {
  listeners.add(listener);
}

function announce(scope: Scope): void {
  listeners.forEach((listener) => listener(scope));
}

export function loadSong(song: TrackerSong): void {
  state.song = song;
  state.pat = 0;
  state.sel = null;
  state.pen = 0;
  past = [];
  future = [];
  lastEdit = { key: "", at: 0 };
  announce("all");
}

export function closeSong(): void {
  state.song = null;
  past = [];
  future = [];
}

export const canUndo = (): boolean => past.length > 0;
export const canRedo = (): boolean => future.length > 0;

// `key` joins quick repeats of one kind (dragging a slider) into a single undo step
export function edit(change: (song: TrackerSong) => void, scope: Scope = "all", key = ""): void {
  const song = state.song;
  if (!song) return;
  const now = Date.now();
  if (!key || key !== lastEdit.key || now - lastEdit.at > 600) {
    past.push(JSON.stringify(song));
    if (past.length > HISTORY) past.shift();
    future = [];
  }
  lastEdit = { key, at: now };
  change(song);
  state.pat = Math.min(state.pat, song.patterns.length - 1);
  scheduleSave();
  announce(scope);
}

function restore(raw: string): void {
  const song = JSON.parse(raw) as TrackerSong;
  state.song = song;
  state.pat = Math.min(state.pat, song.patterns.length - 1);
  state.pen = Math.min(state.pen, Math.max(0, song.sounds.length - 1));
  state.sel = null;
  scheduleSave();
  announce("all");
}

export function undo(): void {
  if (!state.song || !past.length) return;
  future.push(JSON.stringify(state.song));
  restore(past.pop()!);
}

export function redo(): void {
  if (!state.song || !future.length) return;
  past.push(JSON.stringify(state.song));
  restore(future.pop()!);
}

// a change in mode can leave a song with more than the new mode allows. this drops the extras the same way the server will
export function clampToMode(song: TrackerSong): TrackerSong {
  return cleanSong(song, song.updatedAt, { id: song.id, createdAt: song.createdAt });
}

export function scheduleSave(): void {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => void flushSave(), 900);
}

export function flushSave(): Promise<void> {
  window.clearTimeout(saveTimer);
  const song = state.song;
  if (!song) return saving;
  saving = saving.then(async () => {
    try {
      await call(CHANNELS.trackerSave, song);
    } catch (error) {
      flashToast(error instanceof Error ? error.message : t("could not save that song"));
    }
  });
  return saving;
}
